import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, RefreshCw, CheckCircle, AlertCircle } from "lucide-react";

const EmailConfirmationWaiting = () => {
  const { t } = useTranslation();
  const [resendStatus, setResendStatus] = useState("idle");
  const [countdown, setCountdown] = useState(0);
  const resetTimerRef = useRef(null);

  const urlParams = new URLSearchParams(window.location.search);
  const email =
    urlParams.get("email") ||
    localStorage.getItem("pendingEmail") ||
    t("emailConfirmationWaiting.defaultEmail");

  useEffect(() => () => clearTimeout(resetTimerRef.current), []);

  const handleResendEmail = async () => {
    if (countdown > 0) return;
    setResendStatus("loading");

    try {
      const response = await fetch("/api/resend-confirmation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) throw new Error("resend_failed");

      setResendStatus("success");
      setCountdown(60);

      const timer = window.setInterval(() => {
        setCountdown((previous) => {
          if (previous <= 1) {
            clearInterval(timer);
            return 0;
          }
          return previous - 1;
        });
      }, 1000);
    } catch {
      setResendStatus("error");
    }

    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => setResendStatus("idle"), 3000);
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 overflow-hidden">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-12 text-center border-b border-neutral-200">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full shadow-md mb-6">
              <Mail className="w-10 h-10 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-serif text-gray-900 mb-3">
              {t("emailConfirmationWaiting.title")}
            </h1>
            <p className="text-neutral-600 text-lg">
              {t("emailConfirmationWaiting.sentTo")}
            </p>
            <p className="text-neutral-900 font-medium text-lg mt-1">{email}</p>
          </div>

          <div className="p-8 space-y-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-900">
                  <strong className="block mb-1">{t("emailConfirmationWaiting.stepInboxTitle")}</strong>
                  {t("emailConfirmationWaiting.stepInboxDescription")}
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-900">
                  <strong className="block mb-1">{t("emailConfirmationWaiting.spamTitle")}</strong>
                  {t("emailConfirmationWaiting.spamDescription")}
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl border border-green-100">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-green-900">
                  <strong className="block mb-1">{t("emailConfirmationWaiting.stepPaymentTitle")}</strong>
                  {t("emailConfirmationWaiting.stepPaymentDescription")}
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-neutral-200">
              <p className="text-sm text-neutral-600 mb-4 text-center">
                {t("emailConfirmationWaiting.notReceived")}
              </p>

              <button
                onClick={handleResendEmail}
                disabled={countdown > 0 || resendStatus === "loading"}
                className={`w-full py-3 px-6 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                  countdown > 0 || resendStatus === "loading"
                    ? "bg-neutral-100 text-neutral-400 cursor-not-allowed"
                    : "bg-neutral-900 text-white hover:bg-neutral-800"
                }`}
              >
                <RefreshCw className={`w-4 h-4 ${resendStatus === "loading" ? "animate-spin" : ""}`} />
                {resendStatus === "loading"
                  ? t("emailConfirmationWaiting.sending")
                  : countdown > 0
                    ? t("emailConfirmationWaiting.retryIn", { seconds: countdown })
                    : t("emailConfirmationWaiting.resend")}
              </button>

              {resendStatus === "success" && (
                <p className="text-sm text-green-600 text-center mt-3 font-medium">
                  {t("emailConfirmationWaiting.success")}
                </p>
              )}

              {resendStatus === "error" && (
                <p className="text-sm text-red-600 text-center mt-3 font-medium">
                  {t("emailConfirmationWaiting.error")}
                </p>
              )}
            </div>

            <div className="pt-6 border-t border-neutral-200">
              <p className="text-xs text-neutral-500 text-center leading-relaxed">
                <strong className="text-neutral-700">{t("emailConfirmationWaiting.importantLabel")}</strong>{" "}
                {t("emailConfirmationWaiting.importantDescription")}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-neutral-600">
            {t("emailConfirmationWaiting.needHelp")}{" "}
            <a href="/contact" className="text-neutral-900 underline hover:text-black font-medium">
              {t("emailConfirmationWaiting.contactSupport")}
            </a>
          </p>
          <p className="text-xs text-neutral-400 mt-2">
            <a href="/login" className="hover:text-neutral-600 underline">
              {t("emailConfirmationWaiting.backToLogin")}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmationWaiting;
